import React, { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useGLTF, useAnimations } from "@react-three/drei";
import { SkeletonUtils } from "three-stdlib";
import { useFrame } from "@react-three/fiber";
import ErrorBoundary from "@/components/ErrorBoundary";

const COLORS = {
  stanley: 0xe8d6a6,
  brian: 0xc2c9d1,
  stanleyRim: 0xc9a24b,
  brianRim: 0x2c3946,
};

export function CapsuleFigure({ which = "stanley", armRef }) {
  const bodyColor = which === "stanley" ? COLORS.stanley : COLORS.brian;
  const rim = which === "stanley" ? COLORS.stanleyRim : COLORS.brianRim;
  const mat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: bodyColor, roughness: 0.35, metalness: 0.15, emissive: rim, emissiveIntensity: 0.5,
      }),
    [bodyColor, rim]
  );
  return (
    <group>
      <mesh material={mat} position={[0, 0.38, 0]}><capsuleGeometry args={[0.17, 0.5, 6, 12]} /></mesh>
      <mesh material={mat} position={[0, 0.98, 0]}><capsuleGeometry args={[0.24, 0.55, 8, 16]} /></mesh>
      <mesh material={mat} position={[0, 1.45, 0]}><sphereGeometry args={[0.2, 24, 24]} /></mesh>
      <mesh ref={armRef} material={mat} position={[0, 1.15, 0]} rotation={[0, 0, Math.PI / 2]}>
        <capsuleGeometry args={[0.09, 0.5, 6, 10]} />
      </mesh>
    </group>
  );
}

// Rigged, skeletally-animated figure. Loads a base rigged GLB (walk/crouch clip)
// plus a run-clip GLB (same Mixamo skeleton) and crossfades idle/walk/run.
function RiggedFigure({ baseUrl, runUrl, faceSign, getMotion, getRunning, rim }) {
  const base = useGLTF(baseUrl);
  const run = useGLTF(runUrl);
  const group = useRef();
  const { normalized, root } = useMemo(() => {
    const r = SkeletonUtils.clone(base.scene);
    r.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = false; o.receiveShadow = false; o.frustumCulled = false;
        if (o.material) {
          o.material = o.material.clone();
          o.material.side = THREE.FrontSide;
          if (rim) {
            // self-lit gold rim so the rigged survivors stay silhouetted through smoke/dust
            o.material.emissive = new THREE.Color(0xc9a24b);
            o.material.emissiveIntensity = 0.55;
            o.material.toneMapped = true;
          }
        }
      }
    });
    const box = new THREE.Box3().setFromObject(r);
    const size = new THREE.Vector3(); box.getSize(size);
    const scale = size.y > 0 ? 1.72 / size.y : 1;
    r.scale.setScalar(scale);
    const box2 = new THREE.Box3().setFromObject(r);
    const center = new THREE.Vector3(); box2.getCenter(center);
    r.position.set(-center.x, -box2.min.y, -center.z);
    const wrap = new THREE.Group();
    wrap.add(r);
    wrap.rotation.y = faceSign < 0 ? -Math.PI / 2 : Math.PI / 2;
    return { normalized: wrap, root: r };
  }, [base.scene, faceSign, rim]);

  const clips = useMemo(() => {
    const b = base.animations || [];
    const r = run.animations || [];
    const all = run === base ? b : [...b, ...r];
    // strip root translation so the clip plays IN PLACE (no Mixamo root-motion drift)
    return all.map((clip) => {
      const c = clip.clone();
      c.tracks = c.tracks.filter((tr) => !/\.position$/.test(tr.name));
      return c;
    });
  }, [base, run]);
  const { actions, names } = useAnimations(clips, root);
  const curRef = useRef(null);

  useFrame(() => {
    if (!names || names.length === 0) return;
    const running = getRunning ? getRunning() : false;
    const moving = getMotion ? getMotion() !== "idle" : true;
    const runName = names.find((n) => /run/i.test(n));
    const walkName = names.find((n) => /walk|crouch/i.test(n)) || names[0];
    const targetName = running && runName ? runName : walkName;
    const a = actions[targetName];
    if (!a) return;
    if (curRef.current !== a) {
      if (curRef.current) curRef.current.fadeOut(0.25);
      a.reset().fadeIn(0.25).play();
      curRef.current = a;
    }
    // never freeze to bind (T) pose — idle plays the walk clip gently
    a.timeScale = running ? 1.45 : moving ? 1.0 : 0.34;
  });

  return <primitive ref={group} object={normalized} />;
}

function GLBFigure({ url, faceSign, rim }) {
  const gltf = useGLTF(url);
  const { scene, animations } = gltf;
  const { normalized, root } = useMemo(() => {
    const r = scene.clone(true);
    r.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = false; o.receiveShadow = false; o.frustumCulled = false;
        if (o.material) {
          o.material = o.material.clone();
          o.material.side = THREE.FrontSide;
          if (rim) {
            // self-lit gold rim so the survivors stay silhouetted through 100% smoke
            o.material.emissive = new THREE.Color(0xc9a24b);
            o.material.emissiveIntensity = 0.55;
            o.material.toneMapped = true;
          }
        }
      }
    });
    const box = new THREE.Box3().setFromObject(r);
    const size = new THREE.Vector3(); box.getSize(size);
    const scale = size.y > 0 ? 1.72 / size.y : 1;
    r.scale.setScalar(scale);
    const box2 = new THREE.Box3().setFromObject(r);
    const center = new THREE.Vector3(); box2.getCenter(center);
    r.position.set(-center.x, -box2.min.y, -center.z);
    const wrap = new THREE.Group();
    wrap.add(r);
    wrap.rotation.y = faceSign < 0 ? -Math.PI / 2 : Math.PI / 2;
    return { normalized: wrap, root: r };
  }, [scene, faceSign, rim]);

  // Auto-detect & play real Mixamo-style clips if the GLB ships with any.
  const { actions, names } = useAnimations(animations || [], root);
  useEffect(() => {
    if (!actions || !names || names.length === 0) return;
    const walk = names.find((n) => /walk/i.test(n)) || names[0];
    const a = actions[walk];
    if (a) { a.reset().fadeIn(0.3).play(); }
    return () => { if (a) a.fadeOut(0.2); };
  }, [actions, names]);

  return <primitive object={normalized} />;
}

// procedural motion applied to an inner group when no real clips exist
function useProceduralMotion(ref, getMotion, hasClips, getCough) {
  useFrame((state) => {
    const g = ref.current;
    if (!g || hasClips) return;
    const t = state.clock.elapsedTime;
    const m = getMotion ? getMotion() : "idle";
    if (m === "walk") {
      g.position.y = Math.abs(Math.sin(t * 7)) * 0.06;
      g.rotation.z = Math.sin(t * 3.5) * 0.05;
      g.rotation.x = Math.sin(t * 7) * 0.03;
    } else if (m === "run") {
      // high-speed sprint: strong vertical drive, forward lean + torso rock
      g.position.y = Math.abs(Math.sin(t * 11)) * 0.15;
      g.rotation.x = 0.2 + Math.sin(t * 11) * 0.04;
      g.rotation.z = Math.sin(t * 5.5) * 0.07;
    } else if (m === "crawl") {
      g.position.y = -0.35 + Math.abs(Math.sin(t * 5)) * 0.03;
      g.rotation.x = 0.85;
      g.rotation.z = Math.sin(t * 5) * 0.04;
    } else {
      g.position.y += (0 - g.position.y) * 0.1;
      g.rotation.x += (0 - g.rotation.x) * 0.1;
      g.rotation.z = Math.sin(t * 2) * 0.015;
    }
    // respiratory-distress overlay: head/chest tilt forward + a downward hitch
    const c = getCough ? getCough() : 0;
    if (c > 0.001) {
      const hitch = c * (0.85 + 0.15 * Math.sin(t * 40));
      g.rotation.x += hitch * 0.5;
      g.position.y -= hitch * 0.05;
    }
  });
}

export default function Avatar({ which, url, faceSign = 1, armRef, getMotion, getCough, rig, getRunning, rim }) {
  const inner = useRef();
  useProceduralMotion(inner, getMotion, !!rig, getCough);
  const fallback = <CapsuleFigure which={which} armRef={armRef} />;
  return (
    <group ref={inner}>
      <ErrorBoundary fallback={fallback}>
        <Suspense fallback={fallback}>
          {rig ? (
            <RiggedFigure baseUrl={rig.base} runUrl={rig.run} faceSign={faceSign} getMotion={getMotion} getRunning={getRunning} rim={rim} />
          ) : (
            <GLBFigure url={url} faceSign={faceSign} rim={rim} />
          )}
        </Suspense>
      </ErrorBoundary>
    </group>
  );
}

try {
  useGLTF.preload("/models/brian_rig.glb");
  useGLTF.preload("/models/brian_run.glb");
  useGLTF.preload("/models/stanley_rig.glb");
  
} catch (e) {}
